import type { ReportLogicContext } from "./context";
import type {
    ReportConfig,
    MetricSection,
    DataColumn,
    DateGranularity,
    ExcelColumnAxisOptions,
    ExcelDetailColumnOptions,
    WorkbookExcelSheetHeader,
    SectionExcelLayout,
    ExcelColumnLayout,
    ExcelColumnRole,
    ExcelCellFormat,
} from "../types";
import { virtualCategoryColumn, buildCatRulesColumns } from "./ruleInterpretation";
import {
    columnLabel,
    breakdownColumnForModel,
    detailColumnsForModel,
    findSelectedDetailColumn,
    dataColumnSelectionKey,
    isTimestampColumnForModel,
} from "./catalogResolution";
import { granularityLabel } from "./reportingPeriods";
import { defaultColumnAxisOptions, makeDefaultExcelRowLabel, makeDefaultExcelMeasure, normalizeExcelChartLayout } from "./excelLayoutDefaults";
import { extraBreakdownLabel } from "./periodDate";
import { isComboChart, orderedComboSeries, validateComboChart } from "./chartCombo";
import { detailRuleSetOutputs } from "./detailRuleSets";
import { DETAIL_CALC_ALIAS_PREFIX, calculatedFieldValueType, detailCalcAlias, detailCalculatedColumns, isFormulaCalculatedField, measureLabel as calculatedMeasureLabel, usesMeasureField } from "./calculatedFields";
import { detailConstAlias, detailConstantColumns, detailConstantDefaultFormat, detailConstantIssue } from "./detailConstantColumns";
import { combinedRowLabel, combinedRows, usesCombinedRuleSets } from "./combinedRuleSetSelection";

// Combined categorizations (breakdownSource "ruleSets") reuse the pivot layout - a row header,
// one time axis, one measure - but their rows come from MetricSection.combinedRows rather than
// breakdown values, and each row keeps its own format in layout.combinedRowOptions. The layout's
// mode is "combined" so a template or a stale pivot layout can't be mistaken for one.
const COMBINED_ROW_HEADER = "Category";

function layoutModeFor(section: MetricSection): "pivot" | "combined" {
    return usesCombinedRuleSets(section) ? "combined" : "pivot";
}

/** The rows a combined block shows, in sheet order (layout order first, then the configured order), minus hidden ones. */
function combinedSheetRows(section: MetricSection, layout?: SectionExcelLayout | null) {
    const rows = combinedRows(section);
    const order = layout?.combinedRowOrder || [];
    const ordered = order.map((id) => rows.find((row) => row.id === id)).filter(Boolean) as typeof rows;
    const all = ordered.concat(rows.filter((row) => !ordered.includes(row)));
    return all.filter((row) => !layout?.combinedRowOptions?.[row.id]?.hidden);
}

// --- Excel layout support ---------------------------------------------------------
// Bridges a MetricSection's generated SQL shape (one row-label breakdown, zero or one
// extra breakdown used as a time axis, one measure) to a declared Excel placement. See
// types.ts (SectionExcelLayout and friends) for the shape being built here.

function sectionUsesVirtualCategory(context: ReportLogicContext, report: ReportConfig | undefined, section: MetricSection) {
    if (!report || section.breakdownSource !== "ruleSet" || !section.ruleSetId) return false;
    const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
    return !!virtualCategoryColumn(buildCatRulesColumns(context, report, ruleSet));
}

function excelSectionAliases(context: ReportLogicContext, section: MetricSection, report?: ReportConfig): { sourceAlias: string; label: string; extraBreakdownId?: string }[] {
    if (!section.model) return [];
    if (section.displayAs === "detail") return excelDetailSectionAliases(context, section, report);
    if (!section.breakdownColumn) return [];
    const items: { sourceAlias: string; label: string; extraBreakdownId?: string }[] = [];
    if (usesCombinedRuleSets(section)) {
        items.push({ sourceAlias: "breakdown", label: COMBINED_ROW_HEADER });
    } else if (!section.noBreakdown) {
        items.push({ sourceAlias: "breakdown", label: sectionUsesVirtualCategory(context, report, section) ? "Category" : columnLabel(context, section.breakdownColumn) });
    }
    section.extraBreakdowns.forEach((extra) => {
        // Same label rule as before for column breakdowns; Period Date parts read "Period Date (Year)".
        const label = extraBreakdownLabel(context, section, extra);
        items.push({ sourceAlias: `extra:${extra.id}`, label, extraBreakdownId: extra.id });
    });
    const measureLabel = section.displayAs === "percentage" ? "Percentage" : section.displayAs === "otp" ? "On-Time Percentage" : calculatedMeasureLabel(report, section);
    items.push({ sourceAlias: "measure", label: measureLabel });
    return items;
}

// Detail metrics have no breakdown/measure shape - each selected column (in the order
// selected, or every visible DataModelColumn when none are explicitly picked, matching
// the SELECT * fallback in buildSqlForSection) becomes its own "detail:<selectionKey>"
// alias so it can be given its own header/format/order on the sheet.
//
// A detail metric categorized by several rule sets (detailRuleSetIds) also gets one
// "detail:ruleSet:<id>" alias per rule set that produces a Category column, after the data
// columns, so each rule set's column can be ordered/renamed/formatted on the sheet too.
function excelDetailSectionAliases(context: ReportLogicContext, section: MetricSection, report?: ReportConfig): { sourceAlias: string; label: string }[] {
    const allColumns = detailColumnsForModel(context, section.model);
    const source = section.selectedColumns.length
        ? section.selectedColumns.map((key) => findSelectedDetailColumn(allColumns, key)).filter(Boolean) as DataColumn[]
        : allColumns;
    const seen = new Set<string>();
    const items: { sourceAlias: string; label: string }[] = [];
    source.forEach((column) => {
        const selectionKey = dataColumnSelectionKey(column);
        if (!selectionKey || seen.has(selectionKey)) return;
        seen.add(selectionKey);
        items.push({ sourceAlias: `detail:${selectionKey}`, label: column.label });
    });
    detailRuleSetOutputs(context, report, section).forEach((output) => {
        if (!output.columnExpr || seen.has(output.alias)) return;
        seen.add(output.alias);
        items.push({ sourceAlias: output.alias, label: output.label });
    });
    // Calculated fields ("detail:calc:<id>"), after the categorization columns - the same
    // order both SQL builders append them in.
    detailCalculatedColumns(context, report, section).forEach((item) => {
        if (!item.expr || seen.has(item.alias)) return;
        seen.add(item.alias);
        items.push({ sourceAlias: item.alias, label: item.label });
    });
    // Fixed value columns ("detail:const:<id>"), last - again the order both builders use.
    detailConstantColumns(section).forEach((column) => {
        const alias = detailConstAlias(column.id);
        if (detailConstantIssue(section, column) || seen.has(alias)) return;
        seen.add(alias);
        items.push({ sourceAlias: alias, label: (column.name || "").trim() });
    });
    return items;
}

/**
 * A fixed value column's default sheet format follows its type (number, date or text); a
 * text calculated field starts as text, a date-part field as text or a date (Year 2026 must not
 * read "2,026"), and a number calculated field defaults to two decimals.
 */
function constantColumnFormat(section: MetricSection, sourceAlias: string, report?: ReportConfig | null): ExcelCellFormat | undefined {
    const column = detailConstantColumns(section).find((item) => detailConstAlias(item.id) === sourceAlias);
    if (column) return detailConstantDefaultFormat(column.valueType);
    const field = (report?.calculatedFields || []).find((item) => detailCalcAlias(item.id) === sourceAlias);
    if (!field || isFormulaCalculatedField(field)) return undefined;
    return calculatedFieldValueType(field) === "date" ? "MM-dd-yyyy" : "text";
}

function makeDefaultExcelColumnAxis(granularity: DateGranularity = "month"): ExcelColumnAxisOptions {
    return defaultColumnAxisOptions(granularity);
}

// No DataType/DataTypeCategory reaches the frontend catalog today (see
// Report-Configuration-Context.md), so the only signal available for a sensible
// default format is whether the column lives in the model's timestampColumns bucket.
// The user can always override via the Format picker in DetailColumnsSection.
function makeDefaultExcelDetailColumnOptions(context: ReportLogicContext, modelKey: MetricSection["model"], selectionKey: string, label: string, formatOverride?: ExcelCellFormat): ExcelDetailColumnOptions {
    if (formatOverride) return { header: label, include: true, format: formatOverride, widthMode: "auto", wrapText: false };
    // A calculated field is always a number; two decimals is the sensible starting point.
    if (`detail:${selectionKey}`.startsWith(DETAIL_CALC_ALIAS_PREFIX)) {
        return { header: label, include: true, format: "decimal2", widthMode: "auto", wrapText: false };
    }
    const isTimestamp = isTimestampColumnForModel(context, modelKey, selectionKey);
    return { header: label, include: true, format: isTimestamp ? "MM-dd-yyyy" : "text", widthMode: "auto", wrapText: false };
}

const EXCEL_SHEET_INVALID_CHARS = /[:\\/?*\[\]]/;
const EXCEL_DEFAULT_SHEET_NAME = "Report";

interface ExcelSheetMetricRef {
    section: MetricSection;
    index: number;
    order: number;
    enabled: boolean;
}

interface ExcelSheetSummary {
    key: string;
    name: string;
    metrics: ExcelSheetMetricRef[];
    enabledCount: number;
    totalCount: number;
}

function sanitizeExcelSheetName(value: string) {
    let name = String(value || "").trim().replace(/[:\\/?*\[\]]/g, " ");
    name = name.replace(/\s+/g, " ").replace(/^'+|'+$/g, "").trim();
    if (!name) name = EXCEL_DEFAULT_SHEET_NAME;
    if (name.length > 31) name = name.slice(0, 31).trim();
    if (!name) name = EXCEL_DEFAULT_SHEET_NAME;
    if (name.toLowerCase() === "history") name = "Report History";
    return name.length > 31 ? name.slice(0, 31).trim() : name;
}

function defaultExcelSheetName(report: ReportConfig) {
    return sanitizeExcelSheetName(report.label || report.reportKey || EXCEL_DEFAULT_SHEET_NAME);
}

function normalizeExcelSheetName(value: string) {
    return String(value || "").trim();
}

function excelSheetKey(value: string) {
    return normalizeExcelSheetName(value).toLowerCase();
}

function validateExcelSheetName(rawName: string): string | null {
    const name = normalizeExcelSheetName(rawName);
    if (!name) return "Sheet name can't be blank.";
    if (name.length > 31) return "Sheet name can't be longer than 31 characters.";
    if (EXCEL_SHEET_INVALID_CHARS.test(name)) return "Sheet name can't contain any of : \\ / ? * [ ]";
    if (name.startsWith("'") || name.endsWith("'")) return "Sheet name can't start or end with an apostrophe.";
    if (name.toLowerCase() === "history") return '"History" is reserved by Excel. Choose another sheet name.';
    return null;
}

function metricSheetName(report: ReportConfig, section: MetricSection) {
    const assigned = normalizeExcelSheetName(section.excelLayout?.sheetName || "");
    return assigned || defaultExcelSheetName(report);
}

function excelLayoutOrder(section: MetricSection, index: number) {
    return section.excelLayout?.blockOrder ?? (index + 1) * 10;
}

function excelSheetsInUse(report: ReportConfig): ExcelSheetSummary[] {
    const defaultName = defaultExcelSheetName(report);
    const byKey = new Map<string, ExcelSheetSummary>();
    const firstUseKeys: string[] = [];

    const ensureSheet = (name: string) => {
        const normalized = normalizeExcelSheetName(name) || defaultName;
        const key = excelSheetKey(normalized);
        let sheet = byKey.get(key);
        if (!sheet) {
            sheet = { key, name: normalized, metrics: [], enabledCount: 0, totalCount: 0 };
            byKey.set(key, sheet);
            firstUseKeys.push(key);
        }
        return sheet;
    };

    if (!report.sections.length) {
        ensureSheet(defaultName);
    }

    report.sections.forEach((section, index) => {
        const sheet = ensureSheet(metricSheetName(report, section));
        const enabled = !!section.excelLayout?.enabled;
        sheet.metrics.push({ section, index, order: excelLayoutOrder(section, index), enabled });
        sheet.totalCount += 1;
        if (enabled) sheet.enabledCount += 1;
    });

    const outputKeys = report.sections
        .map((section, index) => ({ section, index, order: excelLayoutOrder(section, index) }))
        .filter((item) => item.section.excelLayout?.enabled)
        .sort((left, right) => left.order - right.order || left.index - right.index)
        .map((item) => excelSheetKey(metricSheetName(report, item.section)));

    const storedOrder = report.excelWorkbook?.sheetOrder || [];
    const orderedKeys: string[] = [];
    [...storedOrder, ...outputKeys, ...firstUseKeys].forEach((key) => {
        const normalizedKey = excelSheetKey(key);
        if (!orderedKeys.includes(normalizedKey)) orderedKeys.push(normalizedKey);
    });

    return orderedKeys
        .map((key) => byKey.get(key))
        .filter((sheet): sheet is ExcelSheetSummary => !!sheet)
        .map((sheet) => ({
            ...sheet,
            metrics: sheet.metrics.slice().sort((left, right) => left.order - right.order || left.index - right.index),
        }));
}

function metricPositionWithinSheet(report: ReportConfig, sectionId: string) {
    const sheet = excelSheetsInUse(report).find((candidate) => candidate.metrics.some((metric) => metric.section.id === sectionId));
    if (!sheet) return null;
    const index = sheet.metrics.findIndex((metric) => metric.section.id === sectionId);
    if (index < 0) return null;
    return { sheetName: sheet.name, position: index + 1, total: sheet.metrics.length };
}

function storedExcelSheetName(report: ReportConfig, name: string) {
    const normalized = normalizeExcelSheetName(name);
    return excelSheetKey(normalized) === excelSheetKey(defaultExcelSheetName(report)) ? "" : normalized;
}

function excelWorkbookWithSheetHeaders(report: ReportConfig, sheetHeaders: Record<string, WorkbookExcelSheetHeader>) {
    return {
        titleTemplate: report.excelWorkbook?.titleTemplate || "",
        header: report.excelWorkbook?.header,
        sheetHeaders,
        sheetOrder: report.excelWorkbook?.sheetOrder || [],
    };
}

function setMetricExcelSheet(context: ReportLogicContext, report: ReportConfig, sectionId: string, sheetName: string): ReportConfig {
    const stored = storedExcelSheetName(report, sheetName);
    return {
        ...report,
        sections: report.sections.map((section) => {
            if (section.id !== sectionId) return section;
            const layout = section.excelLayout || makeDefaultExcelLayout(context, section, report);
            return { ...section, excelLayout: { ...layout, sheetName: stored } };
        }),
    };
}

function moveExcelSheet(context: ReportLogicContext, report: ReportConfig, fromSheetName: string, toSheetName: string): ReportConfig {
    const fromKey = excelSheetKey(fromSheetName);
    const stored = storedExcelSheetName(report, toSheetName);
    const nextHeaders = Object.entries(report.excelWorkbook?.sheetHeaders || {}).reduce((acc, [key, value]) => {
        if (excelSheetKey(key) !== fromKey) acc[key] = value;
        return acc;
    }, {} as Record<string, WorkbookExcelSheetHeader>);
    const workbook = excelWorkbookWithSheetHeaders(report, nextHeaders);
    workbook.sheetOrder = (workbook.sheetOrder || []).filter((key) => excelSheetKey(key) !== fromKey);
    return {
        ...report,
        sections: report.sections.map((section) => {
            if (excelSheetKey(metricSheetName(report, section)) !== fromKey) return section;
            const layout = section.excelLayout || makeDefaultExcelLayout(context, section, report);
            return { ...section, excelLayout: { ...layout, sheetName: stored } };
        }),
        excelWorkbook: workbook,
    };
}

function renameExcelSheet(context: ReportLogicContext, report: ReportConfig, fromSheetName: string, toSheetName: string): ReportConfig {
    const fromKey = excelSheetKey(fromSheetName);
    const toKey = excelSheetKey(toSheetName);
    const stored = storedExcelSheetName(report, toSheetName);
    const headers = { ...(report.excelWorkbook?.sheetHeaders || {}) };
    if (headers[fromKey]) {
        headers[toKey] = headers[fromKey];
        delete headers[fromKey];
    }
    const workbook = excelWorkbookWithSheetHeaders(report, headers);
    workbook.sheetOrder = (workbook.sheetOrder || []).map((key) => (excelSheetKey(key) === fromKey ? toKey : excelSheetKey(key)));
    return {
        ...report,
        sections: report.sections.map((section) => {
            if (excelSheetKey(metricSheetName(report, section)) !== fromKey) return section;
            const layout = section.excelLayout || makeDefaultExcelLayout(context, section, report);
            return { ...section, excelLayout: { ...layout, sheetName: stored } };
        }),
        excelWorkbook: workbook,
    };
}

function reorderExcelSheets(report: ReportConfig, draggedSheetKey: string, targetSheetKey: string, placement: "before" | "after" = "before"): ReportConfig {
    const fromKey = excelSheetKey(draggedSheetKey);
    const toKey = excelSheetKey(targetSheetKey);
    if (!fromKey || !toKey || fromKey === toKey) return report;

    const currentKeys = excelSheetsInUse(report).map((sheet) => sheet.key);
    if (!currentKeys.includes(fromKey) || !currentKeys.includes(toKey)) return report;

    const nextKeys = currentKeys.filter((key) => key !== fromKey);
    const targetIndex = nextKeys.indexOf(toKey);
    nextKeys.splice(placement === "after" ? targetIndex + 1 : targetIndex, 0, fromKey);

    return {
        ...report,
        excelWorkbook: {
            titleTemplate: report.excelWorkbook?.titleTemplate || "",
            header: report.excelWorkbook?.header,
            sheetHeaders: report.excelWorkbook?.sheetHeaders || {},
            sheetOrder: nextKeys,
        },
    };
}

function reorderExcelMetrics(context: ReportLogicContext, report: ReportConfig, sheetName: string, draggedSectionId: string, targetSectionId: string, placement: "before" | "after" = "before"): ReportConfig {
    if (!draggedSectionId || !targetSectionId || draggedSectionId === targetSectionId) return report;

    const sheetKey = excelSheetKey(sheetName);
    const sheet = excelSheetsInUse(report).find((candidate) => candidate.key === sheetKey);
    if (!sheet) return report;

    const orderedIds = sheet.metrics.map((metric) => metric.section.id);
    if (!orderedIds.includes(draggedSectionId) || !orderedIds.includes(targetSectionId)) return report;

    const nextIds = orderedIds.filter((id) => id !== draggedSectionId);
    const targetIndex = nextIds.indexOf(targetSectionId);
    nextIds.splice(placement === "after" ? targetIndex + 1 : targetIndex, 0, draggedSectionId);
    const orderById = new Map(nextIds.map((id, index) => [id, (index + 1) * 10]));

    return {
        ...report,
        sections: report.sections.map((section) => {
            const nextOrder = orderById.get(section.id);
            if (!nextOrder) return section;
            const layout = section.excelLayout || makeDefaultExcelLayout(context, section, report);
            return { ...section, excelLayout: { ...layout, blockOrder: nextOrder } };
        }),
    };
}

// Builds a first-pass layout: the primary breakdown becomes the row label, the first
// extra breakdown that already has a date granularity becomes the time axis, the
// measure column is set up per displayAs, and anything else starts out ignored so it
// doesn't show up on the sheet until the user deliberately assigns it a role.
//
// Detail metrics take a different shape entirely (see excelDetailSectionAliases): every
// selected column starts out included, in selection order, with a default header/format
// and no derived rows or chart (neither concept applies to a flat row dump).
function makeDefaultExcelLayout(context: ReportLogicContext, section: MetricSection, report?: ReportConfig): SectionExcelLayout {
    const aliases = excelSectionAliases(context, section, report);
    if (section.displayAs === "detail") {
        const columns: ExcelColumnLayout[] = aliases.map((item) => {
            const selectionKey = item.sourceAlias.slice("detail:".length);
            return { sourceAlias: item.sourceAlias, label: item.label, role: "detailColumn" as ExcelColumnRole, detail: makeDefaultExcelDetailColumnOptions(context, section.model, selectionKey, item.label, constantColumnFormat(section, item.sourceAlias, report)) };
        });
        return { enabled: false, sheetName: "", title: section.name || "Untitled metric", blockOrder: 10, mode: "detail", columns, derivedRows: [], chart: normalizeExcelChartLayout() };
    }
    let assignedColumnAxis = false;
    const columns: ExcelColumnLayout[] = aliases.map((item) => {
        if (item.sourceAlias === "breakdown") {
            return { sourceAlias: item.sourceAlias, label: item.label, role: "rowLabel" as ExcelColumnRole, rowLabel: makeDefaultExcelRowLabel(item.label) };
        }
        if (item.sourceAlias === "measure") {
            // A Sum / Average / Min / Max measure is rarely a whole number: start at 2 decimals.
            const measure = makeDefaultExcelMeasure(section.displayAs);
            return { sourceAlias: item.sourceAlias, label: item.label, role: "measure" as ExcelColumnRole, measure: usesMeasureField(section) && section.displayAs === "count" ? { ...measure, numberFormat: "decimal2" as const } : measure };
        }
        const extra = section.extraBreakdowns.find((candidate) => candidate.id === item.extraBreakdownId);
        if (extra?.granularity && !assignedColumnAxis) {
            assignedColumnAxis = true;
            const axis = makeDefaultExcelColumnAxis(extra.granularity);
            // A combined table reads as a scorecard: every period shown, with a Total column.
            const columnAxis = usesCombinedRuleSets(section) ? { ...axis, fillMissingPeriods: true, showTotalColumn: true } : axis;
            return { sourceAlias: item.sourceAlias, label: item.label, role: "columnAxis" as ExcelColumnRole, extraBreakdownId: item.extraBreakdownId, columnAxis };
        }
        return { sourceAlias: item.sourceAlias, label: item.label, role: "ignore" as ExcelColumnRole, extraBreakdownId: item.extraBreakdownId };
    });
    return { enabled: false, sheetName: "", title: section.name || "Untitled metric", blockOrder: 10, mode: layoutModeFor(section), columns, derivedRows: [], chart: normalizeExcelChartLayout() };
}

// Keeps an existing layout's column list in sync with the section's current SQL shape
// (extra breakdowns can be added/removed/re-granularity'd after the layout was first
// built) while preserving whatever role/options the user already chose for aliases
// that still exist. Aliases that are new (e.g. a freshly-added extra breakdown) start
// out ignored, same as makeDefaultExcelLayout.
//
// Detail layouts are reconciled differently: pivot layouts are always rebuilt in
// excelSectionAliases order (harmless there, since that order IS the display order),
// but for a detail layout the array order IS the user's chosen column order, so it must
// be preserved. Existing columns keep their position; columns no longer selected are
// dropped; newly selected columns are appended at the end, defaulted.
function reconcileExcelLayoutColumns(context: ReportLogicContext, section: MetricSection, layout: SectionExcelLayout, report?: ReportConfig): SectionExcelLayout {
    const aliases = excelSectionAliases(context, section, report);
    // Branch on the section's CURRENT displayAs, not layout.mode: a metric can change
    // "Show As" after its layout was built (or have a mismatched template applied - the
    // template picker is scoped by data model + column, not by displayAs), and
    // reconciliation needs to self-heal to match reality rather than staying pinned to
    // whatever mode the layout happened to be built in. If displayAs no longer says
    // "detail", the pivot branch below naturally resets any leftover "detail:" columns
    // (they won't match the now-pivot aliases) rather than misinterpreting them.
    if (section.displayAs === "detail") {
        const byAlias = new Map(aliases.map((item) => [item.sourceAlias, item]));
        const bySourceAlias = new Map(layout.columns.map((column) => [column.sourceAlias, column]));
        const columns: ExcelColumnLayout[] = layout.columns
            .filter((column) => byAlias.has(column.sourceAlias))
            .map((column) => ({ ...column, label: byAlias.get(column.sourceAlias)!.label }));
        aliases.forEach((item) => {
            if (bySourceAlias.has(item.sourceAlias)) return;
            const selectionKey = item.sourceAlias.slice("detail:".length);
            columns.push({ sourceAlias: item.sourceAlias, label: item.label, role: "detailColumn" as ExcelColumnRole, detail: makeDefaultExcelDetailColumnOptions(context, section.model, selectionKey, item.label, constantColumnFormat(section, item.sourceAlias, report)) });
        });
        return { ...layout, mode: "detail", columns, chart: normalizeExcelChartLayout(layout.chart) };
    }
    const bySourceAlias = new Map(layout.columns.map((column) => [column.sourceAlias, column]));
    const columns: ExcelColumnLayout[] = aliases.map((item) => {
        const existing = bySourceAlias.get(item.sourceAlias);
        // A combined block always needs its row header and value columns, even when the layout
        // was built for another shape (or had them ignored) - both are hidden from the editor
        // in combined mode, so the user couldn't fix them by hand.
        if (usesCombinedRuleSets(section) && item.sourceAlias === "breakdown") {
            return { ...existing, sourceAlias: item.sourceAlias, label: item.label, role: "rowLabel" as ExcelColumnRole, rowLabel: existing?.rowLabel || makeDefaultExcelRowLabel(item.label) };
        }
        if (usesCombinedRuleSets(section) && item.sourceAlias === "measure") {
            return { ...existing, sourceAlias: item.sourceAlias, label: item.label, role: "measure" as ExcelColumnRole, measure: { ...makeDefaultExcelMeasure("count"), ...(existing?.measure || {}), numberFormat: "accountingInt" as const, scale: "raw" as const } };
        }
        if (existing) return { ...existing, label: item.label, extraBreakdownId: item.extraBreakdownId };
        return { sourceAlias: item.sourceAlias, label: item.label, role: "ignore" as ExcelColumnRole, extraBreakdownId: item.extraBreakdownId };
    });
    return { ...layout, mode: layoutModeFor(section), columns, chart: normalizeExcelChartLayout(layout.chart) };
}

// Moves a detail layout column one position up (-1) or down (+1) in the column order,
// clamped at the ends. No-op for anything that isn't a detail layout - pivot column
// order is derived from excelSectionAliases, not user-chosen.
function moveExcelDetailColumn(layout: SectionExcelLayout, sourceAlias: string, direction: -1 | 1): SectionExcelLayout {
    if (layout.mode !== "detail") return layout;
    const index = layout.columns.findIndex((column) => column.sourceAlias === sourceAlias);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= layout.columns.length) return layout;
    const columns = layout.columns.slice();
    const [moved] = columns.splice(index, 1);
    columns.splice(targetIndex, 0, moved);
    return { ...layout, columns };
}

// Human-readable problems with the current layout - surfaced inline in the editor and
// in the live preview, not hard validation errors (the workbook-generation endpoint
// enforces the load-bearing ones server-side: exactly one measure, at least one row
// label, at most one time axis).
function excelLayoutValidationMessages(section: MetricSection): string[] {
    if (!section.excelLayout) return [];
    const layout = section.excelLayout;
    const messages: string[] = [];
    const sheetProblem = layout.sheetName ? validateExcelSheetName(layout.sheetName) : null;
    if (sheetProblem) messages.push(sheetProblem);

    // Branch on section.displayAs, not layout.mode: mode can be stale (e.g. "Show As"
    // changed since the layout was last touched, or a mismatched template was applied -
    // see reconcileExcelLayoutColumns), and displayAs is the live source of truth.
    if (section.displayAs === "detail") {
        const included = layout.columns.filter((column) => column.detail?.include);
        if (!included.length) messages.push("Needs at least one column included on the sheet.");
        if (!section.selectedColumns.length) {
            messages.push("No specific columns are selected on the Metrics tab, so the query uses SELECT * - this list reflects every visible column for the data model.");
        }
        const headerCounts = new Map<string, number>();
        included.forEach((column) => {
            const header = (column.detail?.header || column.label).trim();
            if (!header) return;
            headerCounts.set(header, (headerCounts.get(header) || 0) + 1);
        });
        headerCounts.forEach((count, header) => {
            if (count > 1) messages.push(`"${header}" is used as the header for ${count} columns - consider giving each a unique name.`);
        });
        layout.columns.forEach((column) => {
            if (column.detail?.widthMode !== "fixed") return;
            const width = column.detail.width ?? 0;
            if (!width || width < 1 || width > 255) messages.push(`"${column.detail.header || column.label}" needs a fixed width between 1 and 255.`);
        });
        return messages;
    }

    const measures = layout.columns.filter((column) => column.role === "measure");
    const rowLabels = layout.columns.filter((column) => column.role === "rowLabel");
    const columnAxes = layout.columns.filter((column) => column.role === "columnAxis");
    if (measures.length !== 1) messages.push(`Needs exactly one column marked "Value" (found ${measures.length}).`);
    if (!rowLabels.length) messages.push('Needs at least one column marked "Row Label".');
    if (columnAxes.length > 1) messages.push('Only one "Time Axis" column is supported.');
    if (usesCombinedRuleSets(section) && combinedRows(section).length && !combinedSheetRows(section, layout).length) {
        messages.push("Every row of this combined table is hidden - show at least one.");
    }
    measures.forEach((column) => {
        if ((section.displayAs === "percentage" || section.displayAs === "otp") && column.measure?.scale !== "divide100") {
            messages.push(`"${column.label}" comes back from SQL as 0-100 - set its scale to "Divide by 100" so a percent format displays correctly.`);
        }
    });
    layout.derivedRows.forEach((row) => {
        if (row.kind !== "constant" && !row.sourceRowLabels.length) messages.push(`Derived row "${row.label || row.kind}" has no source rows selected.`);
    });
    const chartSettings = layout.chart?.settings;
    if (layout.chart?.enabled && chartSettings) {
        const minimum = chartSettings.valueAxisMinimum;
        const maximum = chartSettings.valueAxisMaximum;
        if (minimum != null && maximum != null && minimum >= maximum) messages.push("Chart minimum value must be less than its maximum value.");
        const interval = chartSettings.valueAxisInterval;
        if (interval != null && !(Number.isFinite(interval) && interval > 0)) messages.push("Chart Y-axis interval must be more than 0.");
        if (chartSettings.widthColumns != null && (chartSettings.widthColumns < 6 || chartSettings.widthColumns > 30)) messages.push("Chart width must be between 6 and 30 columns.");
        if (chartSettings.heightRows != null && (chartSettings.heightRows < 8 || chartSettings.heightRows > 30)) messages.push("Chart height must be between 8 and 30 rows.");
        if (chartSettings.gapWidth < 0 || chartSettings.gapWidth > 500) messages.push("Chart column spacing must be between 0 and 500.");
    }
    messages.push(...validateComboChart(layout.chart));
    return messages;
}

function excelLayoutColumnHeaders(layout: SectionExcelLayout): string[] {
    const axis = layout.columns.find((column) => column.role === "columnAxis");
    if (!axis?.columnAxis) return ["Value"];
    const sample: Record<DateGranularity, string[]> = {
        month: ["Jan", "Feb", "Mar", "…", "Dec"],
        quarter: ["Q1", "Q2", "Q3", "Q4"],
        year: ["…"],
        week: ["Wk 1", "Wk 2", "…"],
        day: ["…"],
    };
    const headers = sample[axis.columnAxis.granularity] || ["…"];
    return axis.columnAxis.showTotalColumn ? [...headers, "Total"] : headers;
}

// Sample cell text for a detail column's format, for the no-SQL-required preview -
// mirrors the three format families in ExcelCellFormat (plain text, numeric, date).
function sampleDetailCellText(format?: ExcelCellFormat) {
    if (!format || format === "text") return "abc";
    if (format === "MM-dd-yyyy" || format === "MM-dd-yyyy hh:mm AM/PM" || format === "yyyy-MM-dd" || format === "hh:mm AM/PM" || format === "raw") return "…";
    return "#";
}

// Detail-mode counterpart to the pivot preview below: no row label / time axis / measure
// concept, just the included columns' headers in their chosen order, plus a few sample
// rows shaped like the column formats. Derived rows and charts don't apply to a flat
// row dump, so neither is rendered here.
function buildExcelDetailLayoutPreview(section: MetricSection, layout: SectionExcelLayout): string {
    const included = layout.columns.filter((column) => column.detail?.include);
    const title = layout.title ?? (section.name || "Untitled metric");
    // Matches the single-line "nothing to show" shape used above (layout disabled): no
    // second line means ExcelLayoutPreview's header check sees zero headers and renders
    // its empty state instead of a one-column table.
    if (!included.length) return `${title.trim() ? `${title} - no` : "No"} columns are included on the sheet yet.`;
    const lines: string[] = [title];
    lines.push(included.map((column) => column.detail?.header?.trim() || column.label).join("\t"));
    for (let row = 0; row < 3; row++) {
        lines.push(included.map((column) => sampleDetailCellText(column.detail?.format)).join("\t"));
    }
    // Aggregated columns with a grand-total row (logic/detailAggregates.ts): the label sits in
    // the first column that is not aggregated, Count Distinct has no total.
    const aggregated = (column: ExcelColumnLayout) => !!column.detail?.aggregate && column.detail.aggregate !== "none";
    if (layout.detailTotals?.show && included.some(aggregated)) {
        const labelAt = included.findIndex((column) => !aggregated(column));
        lines.push(included.map((column, index) => index === labelAt
            ? (layout.detailTotals?.label?.trim() || "Total")
            : aggregated(column) && column.detail?.aggregate !== "countDistinct" ? sampleDetailCellText(column.detail?.format) : "").join("\t"));
    }
    const problems = excelLayoutValidationMessages(section);
    if (problems.length) {
        lines.push("");
        lines.push("Needs attention:");
        problems.forEach((message) => lines.push(`- ${message}`));
    }
    return lines.join("\n");
}

// A quick, no-SQL-required skeleton of what the block will look like, for the editor's
// live preview. Uses the section's own configured groups/include list as stand-in row
// labels when no live data is available yet.
function buildExcelLayoutPreview(section: MetricSection): string {
    if (!section.excelLayout || !section.excelLayout.enabled) {
        return "Excel layout is off for this metric - it will not appear in the generated workbook.";
    }
    const layout = section.excelLayout;
    // Same rationale as excelLayoutValidationMessages above: branch on the live
    // section.displayAs, not the possibly-stale layout.mode.
    if (section.displayAs === "detail") return buildExcelDetailLayoutPreview(section, layout);
    const rowLabelColumn = layout.columns.find((column) => column.role === "rowLabel");
    const headers = excelLayoutColumnHeaders(layout);
    const lines: string[] = [];
    lines.push(layout.title ?? (section.name || "Untitled metric"));
    lines.push([rowLabelColumn?.rowLabel?.displayLabel || "Category", ...headers].join("\t"));
    if (usesCombinedRuleSets(section)) {
        combinedSheetRows(section, layout).forEach((row) => {
            const format = layout.combinedRowOptions?.[row.id]?.numberFormat || (row.value === "share" ? "percent0" : "accountingInt");
            const cell = format.startsWith("percent") ? "%" : "#";
            lines.push([combinedRowLabel(row), ...headers.map((header) => header === "Total" ? (row.value === "share" ? "% (recalculated)" : "# (sum)") : cell)].join("\t"));
        });
        layout.derivedRows.forEach((row) => lines.push(`${row.label || row.kind}\t[derived]`));
        const problems = excelLayoutValidationMessages(section);
        if (problems.length) {
            lines.push("");
            lines.push("Needs attention:");
            problems.forEach((message) => lines.push(`- ${message}`));
        }
        return lines.join("\n");
    }
    const sampleRows = section.groups.length
        ? section.groups.map((group) => group.name)
        : section.include.length
            ? section.include
            : ["(category 1)", "(category 2)", "…"];
    sampleRows.slice(0, 4).forEach((label) => {
        lines.push([label, ...headers.map(() => "#")].join("\t"));
    });
    if (rowLabelColumn?.rowLabel?.showTotalRow) {
        lines.push([rowLabelColumn.rowLabel.totalLabel || "Total", ...headers.map(() => "#")].join("\t"));
    }
    layout.derivedRows.forEach((row) => {
        const formula = row.kind === "sumOf" ? `sum of ${row.sourceRowLabels.join(" + ")}`
            : row.kind === "ratio" ? `${row.sourceRowLabels[0] || "?"} ÷ ${row.sourceRowLabels[1] || "?"}`
            : row.kind === "difference" ? `${row.sourceRowLabels[0] || "?"} - ${row.sourceRowLabels[1] || "?"}`
            : row.kind === "remainderOf" ? `${row.sourceRowLabels[0] || "?"} minus (${row.sourceRowLabels.slice(1).join(", ")})`
            : `constant ${row.constantValue ?? 0}`;
        lines.push(`${row.label || row.kind}\t[${formula}]`);
    });
    if (layout.chart.enabled && isComboChart(layout.chart)) {
        const series = orderedComboSeries(layout.chart.combo)
            .filter((item) => item.seriesKey.trim())
            .map((item) => `${item.seriesKey.trim()} (${item.chartType === "line" ? "line" : "column"}${item.axis === "secondary" ? ", right axis" : ""})`);
        lines.push("");
        lines.push(`Chart: combo, series = ${series.length ? series.join(", ") : "none listed yet"}`);
    } else if (layout.chart.enabled && layout.chart.type !== "none") {
        lines.push("");
        lines.push(`Chart: ${layout.chart.type}, series = ${layout.chart.seriesRowLabels.length ? layout.chart.seriesRowLabels.join(", ") : "every row above"}`);
    }
    const problems = excelLayoutValidationMessages(section);
    if (problems.length) {
        lines.push("");
        lines.push("Needs attention:");
        problems.forEach((message) => lines.push(`- ${message}`));
    }
    return lines.join("\n");
}

export {
    combinedSheetRows,
    sectionUsesVirtualCategory,
    excelSectionAliases,
    excelDetailSectionAliases,
    makeDefaultExcelColumnAxis,
    makeDefaultExcelDetailColumnOptions,
    EXCEL_SHEET_INVALID_CHARS,
    EXCEL_DEFAULT_SHEET_NAME,
    sanitizeExcelSheetName,
    defaultExcelSheetName,
    normalizeExcelSheetName,
    excelSheetKey,
    validateExcelSheetName,
    metricSheetName,
    excelLayoutOrder,
    excelSheetsInUse,
    metricPositionWithinSheet,
    storedExcelSheetName,
    excelWorkbookWithSheetHeaders,
    setMetricExcelSheet,
    moveExcelSheet,
    renameExcelSheet,
    reorderExcelSheets,
    reorderExcelMetrics,
    makeDefaultExcelLayout,
    reconcileExcelLayoutColumns,
    moveExcelDetailColumn,
    excelLayoutValidationMessages,
    excelLayoutColumnHeaders,
    sampleDetailCellText,
    buildExcelDetailLayoutPreview,
    buildExcelLayoutPreview,
};
export type { ExcelSheetMetricRef, ExcelSheetSummary };
